import React, { useState } from 'react'
import styled from 'styled-components'
import { Text } from 'components/Box'
import CopyToClipboard from 'components/CopyToClipboard'
import Popover, { PopoverChildrenProps } from 'components/Popover'
import Color from '../../../Attribute/components/Color'
import ColorDropdown from 'modules/shares/Inspector/Sidebar/components/ColorDropdown'
import rgbTo, {
  ColorFormat,
} from 'modules/shares/Inspector/Sidebar/components/Attribute/components/Color/utils'
import { Gradient } from 'modules/shares/types'

const Stops = styled.div`
  padding: 18px; /* stylelint-disable-line scales/space */
  width: 320px;
`

const TextNoWrap = styled(Text)`
  overflow-wrap: inherit;
`

const StopContainer = styled.div`
  align-items: center;
  display: flex;
  padding-bottom: 5px; /* stylelint-disable-line scales/space */
`

const StopPercentage = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  font-size: 0.8125rem;
  margin-right: 8px;
  text-align: right;
  width: 34px;
`

const Alpha = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  font-size: 0.8125rem;
  margin-left: 4px;
  margin-right: 8px;
`

const StyledColor = styled(Color)`
  margin-right: 8px;
`

interface StopProps {
  stopPosition: number
  red: number
  green: number
  blue: number
  alpha: number
  connected?: boolean
  colorFormat: ColorFormat
  onColorFormatChange: (f: ColorFormat) => void
}

const Stop: React.FC<StopProps> = ({
  stopPosition,
  red,
  green,
  blue,
  alpha,
  connected = true,
  colorFormat,
  onColorFormatChange,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const alphaPercentage = `${(alpha * 100).toFixed()}%`
  const copyValue = rgbTo(colorFormat, { red, green, blue, alpha })

  return (
    <TextNoWrap fontSize="smaller">
      <CopyToClipboard
        value={copyValue}
        placement="left"
        disabled={dropdownVisible}
        showIcon={false}
        dropdown={
          <ColorDropdown
            onChange={newFormat => {
              onColorFormatChange(newFormat)
            }}
            dropdownOpen={setDropdownVisible}
          />
        }>
        <StopContainer>
          <StopPercentage>{`${Math.round(
            stopPosition * 100
          )}%`}</StopPercentage>
          <StyledColor
            red={red}
            green={green}
            blue={blue}
            alpha={alpha}
            displayOpacity={false}
            connected={connected}
            format={colorFormat}
            maxTextWidth={110}
          />
          {alpha !== undefined &&
            alpha !== 1 &&
            colorFormat === ColorFormat.HEX && (
              <Alpha>(Alpha: {alphaPercentage})</Alpha>
            )}
        </StopContainer>
      </CopyToClipboard>
    </TextNoWrap>
  )
}

const gradientColors: (
  gradient: Gradient,
  colorModifiers: {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  }
) => React.ReactNode[] = (gradient, colorModifiers) => {
  const stopsLength = gradient.stops.length
  const stops = gradient.stops
    .sort((lhs, rhs) => lhs.position - rhs.position)
    .map((stop, i) => {
      const connected = stopsLength !== i + 1

      return (
        <Stop
          stopPosition={stop.position}
          red={stop.color.red}
          green={stop.color.green}
          blue={stop.color.blue}
          alpha={stop.color.alpha}
          connected={connected}
          key={stop.position}
          {...colorModifiers}
        />
      )
    })
  return stops
}

interface GradientPopoverProps {
  toggle: PopoverChildrenProps
  gradient: Gradient
  visible: boolean
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  hasScrollBar?: boolean
  onClickOutside?: () => void
  copyValue: string
  dropdown?: React.ReactElement
}

const GradientPopover: React.FC<GradientPopoverProps> = ({
  toggle,
  gradient,
  visible,
  onColorFormatChange,
  colorFormat,
  hasScrollBar,
  onClickOutside,
  copyValue,
  dropdown,
  ...props
}) => {
  return (
    <Popover
      visible={visible}
      placement="top"
      onClickOutside={onClickOutside}
      popup={
        <>
          <Stops>
            {gradientColors(gradient, { onColorFormatChange, colorFormat })}
            <TextNoWrap fontSize="smaller" marginTop="8px">
              <CopyToClipboard
                title="Copy All"
                value={copyValue}
                dropdown={dropdown}
              />
            </TextNoWrap>
          </Stops>
        </>
      }
      spacing="18px"
      data-testid="gradient-popover"
      {...props}>
      {toggle}
    </Popover>
  )
}

export default GradientPopover
